import { z } from 'zod';

/**
 * Supported media storage provider types
 */
export enum MediaStorageProviderType {
  /** No external storage - uses inline base64 (legacy/default for compatibility) */
  INLINE = 'inline',
  /** AWS S3 or S3-compatible storage */
  S3 = 's3',
  /** Google Cloud Storage */
  GCS = 'gcs',
  /** Azure Blob Storage */
  AZURE = 'azure',
}

/**
 * S3 storage configuration schema
 */
export const S3ConfigSchema = z.object({
  bucket: z.string().min(1, 'Bucket name is required'),
  region: z.string().min(1, 'Region is required'),
  endpoint: z.url().optional(),
  accessKeyId: z.string().min(1, 'Access key ID is required'),
  secretAccessKey: z.string().min(1, 'Secret access key is required'),
  forcePathStyle: z.boolean().optional().prefault(false),
  keyPrefix: z.string().optional().prefault('media/'),
});

export type S3Config = z.infer<typeof S3ConfigSchema>;

/**
 * GCS storage configuration schema
 */
export const GCSConfigSchema = z.object({
  bucket: z.string().min(1, 'Bucket name is required'),
  projectId: z.string().min(1, 'Project ID is required'),
  keyFilePath: z.string().optional(),
  credentials: z
    .object({
      clientEmail: z.string(),
      privateKey: z.string(),
    })
    .optional(),
  keyPrefix: z.string().optional().prefault('media/'),
});

export type GCSConfig = z.infer<typeof GCSConfigSchema>;

/**
 * Azure storage configuration schema
 */
export const AzureConfigSchema = z.object({
  containerName: z.string().min(1, 'Container name is required'),
  accountName: z.string().min(1, 'Account name is required'),
  accountKey: z.string().optional(),
  connectionString: z.string().optional(),
  keyPrefix: z.string().optional().prefault('media/'),
});

export type AzureConfig = z.infer<typeof AzureConfigSchema>;

/**
 * Media storage configuration schema
 */
export const MediaStorageConfigSchema = z.discriminatedUnion('provider', [
  z.object({
    provider: z.literal(MediaStorageProviderType.INLINE),
  }),
  z.object({
    provider: z.literal(MediaStorageProviderType.S3),
    config: S3ConfigSchema,
  }),
  z.object({
    provider: z.literal(MediaStorageProviderType.GCS),
    config: GCSConfigSchema,
  }),
  z.object({
    provider: z.literal(MediaStorageProviderType.AZURE),
    config: AzureConfigSchema,
  }),
]);

export type MediaStorageConfig = z.infer<typeof MediaStorageConfigSchema>;
